
import { useState, useEffect, lazy, Suspense } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { NavBar } from "@/components/NavBar";
import { TopicSelector } from "@/components/practice/TopicSelector";
import { WritingArea } from "@/components/practice/WritingArea";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { SubscribeBanner } from "@/components/dashboard/SubscribeBanner";
import { useProfile } from "@/hooks/use-profile";
import { useTopic } from "@/hooks/use-topic";
import { useEvaluation } from "@/components/practice/hooks/useEvaluation";
import { TopicImage } from "@/components/practice/TopicImage";
import { LoadingTopicDisplay } from "@/components/practice/LoadingTopicDisplay";

const Footer = lazy(() => import("@/components/Footer"));

type Topic = {
  id: string;
  topic: string;
  question: string;
  image_url?: string | null;
  task_type: number;
};

const TOPIC_TRANSITION_DELAY = 300;
const DEFAULT_TASK_TYPE = "1";

const BackButton = () => {
  const navigate = useNavigate();

  return (
    <Button
      variant="ghost"
      onClick={() => navigate("/dashboard")}
      className="ml-4 h-5 w-5 p-0 hover:bg-transparent"
    >
      <ChevronLeft className="text-stone-500 dark:text-white" />
      <span className="text-stone-500 dark:text-white">Back</span>
    </Button>
  );
};

const TaskInstructions = ({ taskType }: { taskType: number }) => {
  const instructions = {
    1: {
      time: "20",
      wordCount: "150",
      description: "You will be presented with visual information in the form of a graph, table, chart, or diagram. Your task is to write a summary report describing the main features, trends, and significant points of the data. You should identify important information, describe patterns, make comparisons, and communicate findings in a clear, cohesive manner using academic language."
    },
    2: {
      time: "40",
      wordCount: "250",
      description: "You will need to write a formal essay in response to a point of view, argument, or problem. Present a clear position, develop coherent arguments, and support your ideas with relevant examples from your knowledge or experience. This task evaluates your ability to construct logical arguments and demonstrate appropriate academic vocabulary and grammatical structures."
    }
  };

  const currentTask = instructions[taskType as keyof typeof instructions];

  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-semibold dark:text-white">Task {taskType}</h1>
      <div className="space-y-3 text-stone-500 dark:text-stone-500">
        <div className="space-y-2">
          <p className="font-medium">Time recommended: {currentTask.time} minutes</p>
          <p className="font-medium">Minimum word count: {currentTask.wordCount} words</p>
        </div>
        <p className="leading-relaxed">
          {currentTask.description}
        </p>
      </div>
    </div>
  );
};

const SelectedTopic = ({ topic }: { topic: Topic }) => (
  <div className="flex gap-4">
    <div className="h-24">
      <Separator orientation="vertical" className="h-full dark:border-stone-600" />
    </div>
    <div className="space-y-3">
      <h2 className="text-xl font-semibold dark:text-white">{topic.topic}</h2>
      <p className="text-stone-700 dark:text-stone-500">{topic.question}</p>
    </div>
  </div>
);

const Practice = () => {
  const [searchParams] = useSearchParams();
  const [isChangingTopic, setIsChangingTopic] = useState(false);

  const taskType = parseInt(searchParams.get("task") || DEFAULT_TASK_TYPE);
  const draftId = searchParams.get("draft");
  const preSelectedTopicId = searchParams.get("topic");

  const { profile, isLoading: profileLoading } = useProfile();
  const { data: evaluation } = useEvaluation(draftId);
  const {
    topics,
    selectedTopic,
    selectedTopicId,
    setSelectedTopicId,
    handleRandomTopic,
    isLoadingTopics,
  } = useTopic(taskType, draftId);

  const availableTopics = Array.from(new Set(topics?.map((t) => t.topic) || []));

  useEffect(() => {
    if (preSelectedTopicId && !selectedTopicId) {
      setSelectedTopicId(preSelectedTopicId);
    }
  }, [preSelectedTopicId, selectedTopicId, setSelectedTopicId]);

  const handleTopicSelect = (topicName: string) => {
    setIsChangingTopic(true);
    const topic = topics?.find((t) => t.topic === topicName);
    if (topic) {
      setSelectedTopicId(topic.id);
    }
    setTimeout(() => setIsChangingTopic(false), TOPIC_TRANSITION_DELAY);
  };

  const handleQuestionSelect = (questionId: string) => {
    setIsChangingTopic(true);
    setSelectedTopicId(questionId);
    setTimeout(() => setIsChangingTopic(false), TOPIC_TRANSITION_DELAY);
  };

  const handleStartOver = () => {
    if (selectedTopicId) {
      window.location.href = `/practice?task=${taskType}&topic=${selectedTopicId}`;
    }
  };

  if (profileLoading || isLoadingTopics) {
    return (
      <div className="flex min-h-screen flex-col bg-stone-50 dark:bg-stone-950">
        <NavBar />
        <main className="flex-grow px-4 py-8 max-w-6xl container mx-auto space-y-8">
          <BackButton />
          <TaskInstructions taskType={taskType} />
          <LoadingTopicDisplay />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-stone-50 dark:bg-stone-950">
      <NavBar />

      <main
        className="flex-grow px-4 py-8 max-w-6xl container mx-auto space-y-8"
        aria-labelledby="task-title"
      >
        {profile && !profile.has_paid && <SubscribeBanner />}

        <BackButton />

        <TaskInstructions taskType={taskType} />

        {!evaluation && (
          <TopicSelector
            selectedTopicName={selectedTopic?.topic || null}
            onTopicSelect={handleTopicSelect}
            onQuestionSelect={handleQuestionSelect}
            onRandomQuestion={handleRandomTopic}
            topics={topics || []}
            currentQuestion={selectedTopic}
            availableTopics={availableTopics}
          />
        )}

        {selectedTopic && <SelectedTopic topic={selectedTopic} />}

        {taskType === 1 && selectedTopic?.image_url && (
          <TopicImage imageUrl={selectedTopic.image_url} topic={selectedTopic.topic} />
        )}

        <WritingArea
          topicId={selectedTopicId}
          taskType={taskType}
          isChangingTopic={isChangingTopic}
          onStartOver={handleStartOver}
          topic={selectedTopic}
        />
      </main>

      <Suspense fallback={<div className="h-16" />}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default Practice;
